import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { deleteDoc, doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { FiAlertCircle, FiCheckCircle, FiEdit2, FiFolder, FiPrinter, FiTrash2 } from 'react-icons/fi';
import { db } from '../../lib/firebase';
import { useSessionUser } from '../../lib/permissions';
import { formatDate } from '../../lib/format';
import { archiveDocuments, storedDocuments } from '../../lib/documents';
import DocumentLinks from '../documents/DocumentLinks';
import DocumentViewer from '../documents/DocumentViewer';
import { usePatientDoctorContext } from '../PatientDoctorContext';
import { BUTTON, CARD, EmptyState, Modal, Notice, PageHeader, Panel, Pill, SIZE, SkeletonRows } from '../ui/kit';
import { ECHO_SECTIONS, echoAccess, examDate, filled, operators, patientName, valueOf } from './echoModel';

const display = (field, value) => {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean).join(', ');
  if (field.name === 'sexe') return value === 'F' ? 'Féminin' : value === 'M' ? 'Masculin' : value;
  if (field.type === 'date') return formatDate(value) || value;
  return value;
};

const EchoDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSessionUser();
  const access = echoAccess(user);
  const { patients, ensurePatients } = usePatientDoctorContext();
  const [echo, setEcho] = useState(null);
  const [status, setStatus] = useState('loading');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    ensurePatients().catch(() => {});
    return onSnapshot(
      doc(db, 'echos', id),
      (snapshot) => {
        setEcho(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null);
        setStatus(snapshot.exists() ? 'ready' : 'missing');
      },
      () => setStatus('error')
    );
  }, [id, ensurePatients]);

  const linked = useMemo(() => (echo && echo.patientId ? patients.find((patient) => patient._id === echo.patientId) : null), [echo, patients]);
  const back = { to: '/echo', label: 'Échographies' };

  const remove = async () => {
    setBusy(true);
    setError('');
    try {
      // Its documents go to the archives of the service's Dropbox first (never erased).
      const failed = await archiveDocuments(storedDocuments(echo.documents));
      if (failed.length) {
        await updateDoc(doc(db, 'echos', id), { documents: failed });
        throw new Error('archive');
      }
      await deleteDoc(doc(db, 'echos', id));
      navigate('/echo', { replace: true });
    } catch (err) {
      setError(err.message === 'archive' ? 'Certains documents n’ont pas pu être retirés. Réessayez.' : 'L’échographie n’a pas pu être supprimée.');
      setBusy(false);
    }
  };

  if (status !== 'ready' || !echo) {
    return (
      <main className="mx-auto max-w-5xl px-[20px] pb-16 pt-8 sm:px-8 lg:px-10 lg:pt-10">
        <PageHeader back={back} title={status === 'loading' ? 'Chargement…' : 'Échographie introuvable'} />
        <div className={`${CARD} mt-8 ${status === 'loading' ? 'p-6' : ''}`}>
          {status === 'loading' ? (
            <SkeletonRows rows={4} />
          ) : (
            <EmptyState icon={FiAlertCircle} title={status === 'missing' ? 'Cette échographie n’existe pas ou a été supprimée.' : 'L’échographie n’a pas pu être chargée.'} />
          )}
        </div>
      </main>
    );
  }

  const fe = valueOf(echo, 'mensuration', 'fractionEjection');
  const resume = valueOf(echo, 'conclusion', 'resume');
  const commentaire = valueOf(echo, 'conclusion', 'commentaire');
  const sections = ECHO_SECTIONS.filter((section) => section.id !== 'conclusion');
  const files = Array.isArray(echo.fichiers) ? echo.fichiers.filter((file) => file && file.lien) : [];
  const documents = storedDocuments(echo.documents);

  return (
    <main className="mx-auto max-w-5xl px-[20px] pb-16 pt-8 sm:px-8 lg:px-10 lg:pt-10">
      <PageHeader
        back={back}
        eyebrow={`Écho-doppler cardiaque · ${formatDate(examDate(echo)) || 'date non précisée'}`}
        title={patientName(echo)}
        subtitle={
          <span className="flex flex-wrap gap-1.5">
            {fe && <Pill tone="brand">FE {fe} %</Pill>}
            {operators(echo).map((name) => (
              <Pill key={name}>{name}</Pill>
            ))}
          </span>
        }
        actions={
          <>
            {access.canExport && (
              <button type="button" onClick={() => window.print()} className={`${BUTTON.secondary} ${SIZE.md}`}>
                <FiPrinter size={15} aria-hidden="true" />
                Imprimer
              </button>
            )}
            {access.canDelete && (
              <button type="button" onClick={() => setConfirmDelete(true)} className={`${BUTTON.danger} ${SIZE.md}`}>
                <FiTrash2 size={15} aria-hidden="true" />
                Supprimer
              </button>
            )}
            {access.canWrite && (
              <Link to={`/echo/${echo.id}/modifier`} className={`${BUTTON.primary} ${SIZE.md}`}>
                <FiEdit2 size={15} aria-hidden="true" />
                Modifier
              </Link>
            )}
          </>
        }
      />

      {location.state && location.state.saved && (
        <div className="mt-6 print:hidden">
          <Notice tone="green" icon={FiCheckCircle}>
            Échographie enregistrée.
          </Notice>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-[16px] lg:grid-cols-3">
        <Panel title="Conclusion" className="lg:col-span-2">
          {filled(resume) || filled(commentaire) ? (
            <div className="flex flex-col gap-[14px]">
              {filled(resume) && <p className="m-0 whitespace-pre-wrap break-words text-[15px] leading-relaxed text-ink">{resume}</p>}
              {filled(commentaire) && (
                <div>
                  <p className="m-0 text-[12.5px] font-medium text-black/45">Commentaires</p>
                  <p className="mb-0 mt-0.5 whitespace-pre-wrap break-words text-[14px] leading-relaxed text-black/70">{commentaire}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="m-0 text-[14px] text-black/45">Aucune conclusion saisie.</p>
          )}
        </Panel>
        <Panel title="Dossier patient">
          {linked ? (
            <Link to={`/patients/${linked._id}`} className="-mx-2 flex items-center gap-[12px] rounded-2xl px-2 py-2 text-ink no-underline transition hover:bg-black/[0.03] hover:text-ink">
              <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                <FiFolder size={18} aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-[15px] font-semibold">{linked.nom}</span>
                <span className="block text-[13px] text-black/45">{[linked.dossierNumber && `Dossier ${linked.dossierNumber}`, linked.services].filter(Boolean).join(' · ')}</span>
              </span>
            </Link>
          ) : (
            <p className="m-0 text-[14px] leading-relaxed text-black/50">
              Cette échographie n’est rattachée à aucun dossier.
              {access.canWrite && (
                <>
                  {' '}
                  <Link to={`/echo/${echo.id}/modifier`} className="font-medium text-brand-600 no-underline hover:text-brand-700">
                    La rattacher
                  </Link>
                </>
              )}
            </p>
          )}
        </Panel>
      </div>

      <div className="mt-[16px] grid grid-cols-1 gap-[16px] lg:grid-cols-2">
        {sections.map((section) => {
          const rows = section.fields.map((field) => [field, valueOf(echo, section.group, field.name)]).filter(([, value]) => filled(value));
          if (!rows.length) return null;
          return (
            <Panel key={section.id} title={section.title} className={section.id === 'patient' ? 'lg:col-span-2' : ''}>
              <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-[12px] sm:grid-cols-3">
                {rows.map(([field, value]) => (
                  <div key={field.name} className={field.wide ? 'col-span-2 sm:col-span-3' : ''}>
                    <dt className="text-[12px] font-medium text-black/45">{field.label}</dt>
                    <dd className="mb-0 ml-0 mt-0.5 break-words text-[14.5px] text-ink">
                      <span className="font-medium tabular-nums">{display(field, value)}</span>
                      {field.unit && field.type !== 'select' && <span className="text-black/40"> {field.unit}</span>}
                    </dd>
                  </div>
                ))}
              </dl>
            </Panel>
          );
        })}
      </div>

      {(documents.length > 0 || files.length > 0) && (
        <Panel title="Documents" className="mt-[16px]">
          <DocumentLinks documents={documents} links={files.map((file, index) => ({ link: file.lien, name: file.nom || `Document ${index + 1}` }))} onOpen={setViewing} />
        </Panel>
      )}

      {viewing && <DocumentViewer item={viewing} onClose={() => setViewing(null)} canReplace={access.canWrite} canDownload={access.canExport} />}

      {(echo.createdBy || echo.updatedBy) && (
        <p className="mb-0 mt-[16px] text-right text-[12.5px] text-black/40">
          {echo.createdBy && `Saisie par ${echo.createdBy}`}
          {echo.updatedBy && ` · modifiée par ${echo.updatedBy} le ${formatDate(echo.dateModification)}`}
        </p>
      )}

      <Modal
        open={confirmDelete}
        title="Supprimer l’échographie"
        onClose={() => !busy && setConfirmDelete(false)}
        footer={
          <>
            <button type="button" onClick={() => setConfirmDelete(false)} className={`${BUTTON.secondary} ${SIZE.md}`}>
              Annuler
            </button>
            <button type="button" onClick={remove} disabled={busy} className={`${BUTTON.dangerSolid} ${SIZE.md}`}>
              {busy ? 'Suppression…' : 'Supprimer définitivement'}
            </button>
          </>
        }
      >
        <p className="m-0 text-[14.5px] leading-relaxed text-black/55">
          Le compte rendu d’écho de <strong className="text-ink">{patientName(echo)}</strong> sera supprimé définitivement.
          {storedDocuments(echo.documents).length > 0 && ' Ses documents restent conservés dans les archives du Dropbox du service.'}
        </p>
        {error && (
          <div className="mt-[12px]">
            <Notice tone="red">{error}</Notice>
          </div>
        )}
      </Modal>
    </main>
  );
};

export default EchoDetail;
